import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Chat, LanguageCode, Profile, validateVideo, Video } from "../../models";
import { VideoForm } from "./VideoForm";
import { Button, LinearProgress, Modal } from "@mui/material";
import { getVideo, updateVideo } from "../../api";
import { useSnackbar } from "notistack";

interface VideoFormDialogProps {
  videoId: number;
  languageCodes: LanguageCode[];
  profiles: Profile[];
  chats: Chat[];
  onClose: () => void;
  onVideoSave: () => void;
}

export const VideoFormDialog: React.FC<VideoFormDialogProps> = ({ videoId, languageCodes, profiles, chats, onClose, onVideoSave }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [video, setVideo] = useState<Omit<Video, "id" | "createdAt" | "updatedAt"> | null>(null);
  const [error, setError] = useState<any | null>(null);

  // Form
  const [videoErrors, setVideoErrors] = useState<Record<keyof Video, string>>();
  const [dirty, setDirty] = useState(false);

  const loading = useMemo(() => !video && !error, [video, error]);

  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setError(null);

    try {
      const video = await getVideo(videoId);
      setVideo(video);
    } catch (error) {
      setError(error);
    }
  }, [videoId]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function validate() {
    const errors = validateVideo(video!);

    if (Object.keys(errors).length > 0) {
      setVideoErrors(errors as Record<keyof Video, string>);
    } else {
      setVideoErrors(undefined);
    }

    return Object.keys(errors).length === 0;
  }

  function handleVideoChange(video: Omit<Video, "id" | "createdAt" | "updatedAt">) {
    if (dirty) {
      validate();
    }

    setVideo(video);
  }

  async function handleVideoSave() {
    if (submitting) {
      return;
    }

    if (!validate()) {
      setDirty(true);
      return;
    }

    setDirty(false);

    enqueueSnackbar("Saving...", { variant: "info" });

    setSubmitting(true);

    try {
      await updateVideo(videoId, video!);
      enqueueSnackbar("Video saved", { variant: "success" });
      await fetchData();
      onVideoSave();
    } catch (e) {
      enqueueSnackbar("Failed to save video", { variant: "error" });
      console.error(e);
    }

    setSubmitting(false);
  }

  return (
    <Modal open={true} onClose={onClose}>
      <div className="edit-item-container">
        {loading && <div style={{ width: "50%", margin: "auto" }}>
          <LinearProgress />
        </div>}
        {error && <div>Error: {error}</div>}
        {!loading && !error && <div className="social">
          <div className="box post-edit" style={{ margin: 0 }}>
            <VideoForm
              video={video!}
              onVideoChange={handleVideoChange}
              languageCodes={languageCodes}
              profiles={profiles}
              chats={chats}
              errors={videoErrors}
            >
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onClose}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleVideoSave}
                >
                  Save
                </Button>
                <div>Warning Content in modal maybe closer than they appear</div>
              </>
            </VideoForm>
          </div>
        </div>}
      </div>
    </Modal>
  );
};
